import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { TokenService } from '@services/sys/token.service';
import dayjs from 'dayjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

@Injectable()
export class DefaultInterceptor implements HttpInterceptor {
    private toLoginModelVisible = false;

    constructor(private injector: Injector, private tokenService: TokenService) {
    }

    private get notification(): NzNotificationService {
        return this.injector.get(NzNotificationService);
    }

    private get notifymessage(): NzMessageService {
        return this.injector.get(NzMessageService);
    }

    private get modalmessage(): NzModalService {
        return this.injector.get(NzModalService);
    }

    private goTo(url: string, redirectUrl?: string): void {
        setTimeout(() =>  redirectUrl ? this.injector.get(Router).navigate([url], {
            queryParams: {
                redirect: redirectUrl
            }
        }) : this.injector.get(Router).navigateByUrl(url));
    }

    private checkStatus(ev: HttpResponseBase): void {
        if ((ev.status >= 200 && ev.status < 300) || ev.status === 401) {
            return;
        }

        if (ev instanceof HttpErrorResponse) {
            const message = ev.error.messages?.[0]?.message ?? 'Error';
            if (ev.status !== 500) {
                if (ev.error.messages?.[0]?.type === 'ERROR') {
                    this.notifymessage.error(message, {
                        nzDuration: 2000
                    });
                } else {
                    this.notifymessage.warning(message);
                }
            } else {
                this.notification.remove();
                const referenceMessage = ev.error.messages?.find((item: { message: string, type: string }) => item.message.includes('Error reference'));
                this.notification.error(message, referenceMessage?.message || '', {nzDuration: 10000});
            }
        }
    }

    // #endregion

    private toLogin(redirectUrl?: string): void {
        console.log('toLogin', this.modalmessage);
        if (this.toLoginModelVisible) {
            return;
        }
        this.toLoginModelVisible = true;
        this.notification.remove();
        this.modalmessage.closeAll();
        this.modalmessage.error({
            nzTitle: `Your session has expired. Please login.`,
            nzOnOk: () => {
                this.toLoginModelVisible = false;
                this.goTo('/login', redirectUrl);
            },
            nzOnCancel: () => {
                this.toLoginModelVisible = false;
                console.log('nzOnCancel');
            },
        });
    }

    private handleData(ev: HttpResponseBase, req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        this.checkStatus(ev);
        switch (ev.status) {
            case 200:
                sessionStorage.setItem('lastRequestTime', dayjs().toISOString());
                break;
            case 401:
                const redirectUrl = location.hash.slice(2, location.hash.length);
                this.toLogin(redirectUrl);
                break;
            default:
                if (ev instanceof HttpErrorResponse) {
                    console.warn('Cross-domain issues', ev);
                }
                break;
        }
        if (ev instanceof HttpErrorResponse) {
            return throwError(ev);
        } else if (ev instanceof HttpResponse) {
            if (ev.body.code !== -1) {
                const response: object = Object.assign(ev, { body: ev.body.data });
                return of(new HttpResponse(response));
            } else {
                const message = ev.body.messages?.[0]?.message ?? 'Error';
                if (ev.body.messages?.[0]?.type === 'ERROR') {
                    const referenceMessage = ev.body.messages.find((item: { message: string, type: string }) => item.message.includes('Error reference'));
                    this.notification.error(message, referenceMessage?.message || '', {nzDuration: 10000});
                } else {
                    this.notifymessage.warning(message);
                }
                const response: object = Object.assign(ev);
                return throwError(new HttpErrorResponse(response));
            }
        } else {
            return of(ev);
        }
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let url = req.url;
        if (!url.startsWith('https://') && !url.startsWith('http://') && !url.startsWith(environment.api.mockUrl)) {
            url = environment.api.baseUrl + url;
        }

        const token = this.tokenService.getToken();
        // set xLamtaSession to headers if the token has x-lamta-session
        // set headers only token or x-lamta-session is available\
        let headers = new HttpHeaders();
        if (token) {
            const splitedToken = token.split('/');
            headers = headers.append('token', splitedToken[0]);
            if (splitedToken.length > 1) {
                headers = headers.append('x-lamta-session', splitedToken[1]);
            }
        }

        // Clone the request with new header if token exists.
        const newReq = req.clone({
            url,
            headers: token ? headers : req.headers,
        });


        return next.handle(newReq).pipe(
            mergeMap((ev) => {
                if (req.headers.get('resultIsFile')) {
                    return of (ev);
                }
                if (ev instanceof HttpResponseBase) {
                    return this.handleData(ev, newReq, next);
                }
                return of(ev);
            }),
            catchError((err: HttpErrorResponse) => {
                const hideErrorMessage = req.headers.get('hideErrorMessage');
                if (hideErrorMessage && hideErrorMessage === 'true') {
                    return throwError(err);
                } else {
                    return this.handleData(err, newReq, next);
                }
            }),
        );
    }
}
